// ใบคืนหอพัก (ฉบับพิมพ์) — เอกสารที่นักเรียน ผู้ปกครอง และเจ้าหน้าที่หอพักลงชื่อ
//
// พิมพ์จากแถวในระบบตรงๆ ไม่ให้กรอกใหม่บนกระดาษ ด้วยเหตุผลเดียวกับใบส่งตัวของห้องพยาบาล:
// ถ้ากรอกใหม่ ใบที่ผู้ปกครองถือกับบันทึกในระบบจะไม่ตรงกัน แล้วไม่มีทางรู้ว่าอันไหนถูก
// โดยเฉพาะยอดค่าเสียหาย ซึ่งเป็นตัวเลขที่จะถูกเถียงทีหลัง
//
// ช่องลงชื่อมีสามฝ่ายเพราะการคืนหอมีสามคนที่ต้องยอมรับผลเดียวกัน: นักเรียน (ยอมรับว่า
// ของที่ชำรุดเป็นของตัวเอง) เจ้าหน้าที่หอพัก (ผู้ตรวจ) และหัวหน้าฝ่ายหอพัก (ผู้อนุมัติ)
import React, { useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../services/axiosInterceptor';
import OfficialDocLayout from '../../adminOffice/print/OfficialDocLayout';
import { GROUPS, TYPE_LABEL, baht, resultLabel } from '../clearanceShared';

const MONTHS = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

// อ่านเป็นเวลาท้องถิ่น ไม่ใช่ slice ของสตริง ISO — DATE ที่ผ่าน JSON เป็น UTC ของเมื่อวาน
const fmtThaiFull = (value) => {
  if (!value) return '.................................';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '.................................';
  return `${d.getDate()} ${MONTHS[d.getMonth() + 1]} ${d.getFullYear() + 543}`;
};

const Line = ({ label, value, width = '100%' }) => (
  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
    <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
    <span style={{
      flexGrow: 1, width, borderBottom: '1px dotted #000',
      paddingLeft: 6, minHeight: '1.2em',
    }}>{value || ''}</span>
  </div>
);

const Sign = ({ role, name }) => (
  <div style={{ textAlign: 'center', width: '32%' }}>
    <div>ลงชื่อ ....................................</div>
    <div style={{ marginTop: 6 }}>({name || '.......................................'})</div>
    <div>{role}</div>
  </div>
);

export default function ClearanceDoc() {
  const { dcId } = useParams();
  const [slip, setSlip] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axiosInstance.get(`/api/dormitory/clearance/${dcId}`)
      .then((res) => setSlip(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'));
  }, [dcId]);

  if (error) return <div className="p-4"><Alert variant="danger">{error}</Alert></div>;
  if (!slip) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

  const details = slip.details || [];
  const damage = details.reduce((sum, d) => sum + (Number(d.dcd_amount) || 0), 0);
  const failed = details.filter((d) => d.dcd_result === 'fail');

  return (
    <OfficialDocLayout
      title="ใบคืนหอพักนักเรียน"
      docNo={slip.dc_no}
      backLink={`/dormitory/clearance/${dcId}`}
    >
      <div className="doc-no" style={{ marginTop: -18 }}>
        วันที่คืนหอพัก {fmtThaiFull(slip.dc_date)}
      </div>

      <div style={{ marginTop: 10 }}>
        <Line label="ชื่อ-สกุลนักเรียน" value={slip.st_name} />
        <div style={{ display: 'flex', gap: 16 }}>
          <Line label="เลขประจำตัว" value={slip.st_idnumber} width="30%" />
          <Line label="ชั้น" value={slip.dc_class} width="25%" />
          <Line label="ปีการศึกษา" value={slip.dc_year} width="25%" />
        </div>
        <Line label="หอพัก" value={slip.dc_place} />
        <div style={{ display: 'flex', gap: 16 }}>
          <Line label="เหตุที่ออกจากหอพัก"
            value={[TYPE_LABEL[slip.dc_type] || slip.dc_type, slip.dc_reason].filter(Boolean).join(' — ')} />
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 'bold' }}>ผลการตรวจของฝ่ายหอพัก</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #000', padding: '2px 6px', textAlign: 'left' }}>รายการ</th>
              <th style={{ border: '1px solid #000', padding: '2px 6px', width: '4.5cm' }}>ผลการตรวจ</th>
              <th style={{ border: '1px solid #000', padding: '2px 6px', width: '3cm' }}>ค่าเสียหาย</th>
              <th style={{ border: '1px solid #000', padding: '2px 6px', width: '4.5cm', textAlign: 'left' }}>หมายเหตุ</th>
            </tr>
          </thead>
          <tbody>
            {GROUPS.map((g) => {
              const list = details.filter((d) => d.dcd_group === g.value);
              if (!list.length) return null;
              return (
                <React.Fragment key={g.value}>
                  <tr>
                    <td colSpan={4} style={{
                      border: '1px solid #000', padding: '2px 6px',
                      fontWeight: 'bold', background: '#f0f0f0',
                    }}>{g.label}</td>
                  </tr>
                  {list.map((d) => (
                    <tr key={d.dcd_id}>
                      <td style={{ border: '1px solid #000', padding: '2px 6px' }}>{d.dcd_name}</td>
                      <td style={{ border: '1px solid #000', padding: '2px 6px', textAlign: 'center' }}>
                        {resultLabel(d.dcd_result)}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '2px 6px', textAlign: 'right' }}>
                        {Number(d.dcd_amount) > 0 ? baht(d.dcd_amount) : '-'}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '2px 6px' }}>{d.dcd_note || ''}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            <tr>
              <td colSpan={2} style={{
                border: '1px solid #000', padding: '2px 6px',
                textAlign: 'right', fontWeight: 'bold',
              }}>รวมค่าเสียหาย/ค้างชำระ</td>
              <td style={{
                border: '1px solid #000', padding: '2px 6px',
                textAlign: 'right', fontWeight: 'bold',
              }}>{baht(damage)}</td>
              <td style={{ border: '1px solid #000', padding: '2px 6px' }}>บาท</td>
            </tr>
          </tbody>
        </table>
      </div>

      {slip.dc_note && (
        <div style={{ marginTop: 10 }}>
          <Line label="หมายเหตุ" value={slip.dc_note} />
        </div>
      )}

      <p style={{ marginTop: 14, textIndent: '2.5cm' }}>
        ข้าพเจ้าได้ส่งคืนห้องพักและทรัพย์สินของหอพักตามรายการข้างต้นแล้ว
        {failed.length > 0
          ? ` และรับทราบว่ามีรายการที่ชำรุด/สูญหาย ${failed.length} รายการ
             รวมค่าเสียหายที่ต้องชดใช้ ${baht(damage)} บาท`
          : ' ในสภาพเรียบร้อย ไม่มีรายการที่ต้องชดใช้'}
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 40 }}>
        <Sign role="นักเรียน/ผู้ปกครอง" />
        <Sign role="เจ้าหน้าที่หอพัก (ผู้ตรวจ)"
          name={slip.dc_cleared_by_name || slip.dc_by_name} />
        <Sign role="หัวหน้าฝ่ายหอพัก" />
      </div>

      {slip.dc_status !== 'cleared' && (
        <p style={{ marginTop: 24, textAlign: 'center', fontStyle: 'italic' }}>
          ({slip.dc_status === 'cancelled' ? 'ใบนี้ถูกยกเลิกแล้ว' : 'ยังไม่ผ่านการเซ็นรับรองในระบบ'})
        </p>
      )}
    </OfficialDocLayout>
  );
}
